import moment from "moment";

const firstPresent = (...values) => values.find((value) => value != null && `${value}`.trim()) || "";

export const resolveReservationProperty = (reservation, rentals = []) => {
  const matchingRental = rentals.find(
    (rental) => `${rental?.pms_id || ""}` === `${reservation?.pms_id || ""}`,
  );

  const name = firstPresent(
    reservation?.rental_name,
    reservation?.property_name,
    reservation?.rental?.name,
    matchingRental?.name,
  );
  const address = firstPresent(
    reservation?.rental_address,
    reservation?.property_address,
    reservation?.rental?.address,
    matchingRental?.address,
  );
  const propertyId = firstPresent(
    reservation?.pms_id,
    reservation?.rental_code,
    matchingRental?.pms_id,
  );

  return {
    name,
    address,
    propertyId,
    primary: name || address || propertyId || "Property",
    secondary: name && address ? address : "",
  };
};

export const reservationMatchesPropertySearch = (reservation, rentals, searchTerm) => {
  const normalizedSearch = `${searchTerm || ""}`.trim().toLocaleLowerCase();
  if (!normalizedSearch) return true;

  const property = resolveReservationProperty(reservation, rentals);
  return [property.name, property.address, property.propertyId]
    .filter(Boolean)
    .some((value) => `${value}`.toLocaleLowerCase().includes(normalizedSearch));
};

// Reservation dates are calendar days, not instants in the viewer's timezone.
const reservationDate = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}(?:$|T| )/.test(value)) return null;
  const date = moment.utc(value.slice(0, 10), "YYYY-MM-DD", true);
  return date.isValid() ? date : null;
};

export const formatReservationDate = (value) =>
  reservationDate(value)?.locale("en").format("MMM D, YYYY") || "";

export const reservationStatusLabel = (reservation) => {
  const category = String(reservation.category || "").toUpperCase();
  const status = String(reservation.status || "").toUpperCase();
  if (category === "CXL" || ["CXL", "CANCELED", "CANCELLED"].includes(status)) return "Cancelled";
  if (category === "OWN") return "Owner stay";
  if (category === "BLK") return reservation.reservation_type === "Broker Hold" ? "Broker hold" : "Blocked dates";
  if (category === "STA") return "Active booking";
  return reservation.status || "Status unavailable";
};

export const groupReservations = (reservations, now = new Date()) => {
  // Match Messages' day-level classification, including the entire checkout day.
  const today = moment(now).format("YYYY-MM-DD");
  const grouped = { current: [], upcoming: [], past: [], cancelled: [] };
  for (const reservation of reservations) {
    const checkIn = reservationDate(reservation.check_in)?.format("YYYY-MM-DD");
    const checkOut = reservationDate(reservation.check_out)?.format("YYYY-MM-DD");
    const category = reservationStatusLabel(reservation) === "Cancelled"
      ? "cancelled"
      : checkIn && checkIn > today
        ? "upcoming"
        : checkIn && checkOut && checkIn <= today && checkOut >= today
          ? "current"
          : "past";
    grouped[category].push(reservation);
  }
  return grouped;
};
